import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { BookOpen, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CollectionEmpty,
  CollectionList,
  CollectionLoading,
  CollectionPage,
  CollectionRow,
  FilterInput,
} from "@/components/layout/CollectionPage";
import { get } from "@/lib/api";
import type { ScriptureReference } from "@/types";

/** "Ruth 2:8" or "Ruth 2:8-10" — the reference is what identifies the row. */
function formatReference(reference: ScriptureReference) {
  const base = `${reference.book} ${reference.chapter}:${reference.verse_start}`;
  return reference.verse_end > reference.verse_start
    ? `${base}-${reference.verse_end}`
    : base;
}

export default function ScriptureReferencesPage() {
  const [filterText, setFilterText] = useState("");

  const { data: references = [], isLoading } = useQuery({
    queryKey: ["/api/scripture-references"],
    queryFn: () => get<ScriptureReference[]>("/scripture-references"),
  });

  const visible = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    if (!needle) return references;
    return references.filter(
      (reference) =>
        formatReference(reference).toLowerCase().includes(needle) ||
        reference.text?.toLowerCase().includes(needle),
    );
  }, [references, filterText]);

  const hasFilters = filterText.trim().length > 0;

  return (
    <CollectionPage
      title="Scripture"
      count={visible.length}
      noun="reference"
      isLoading={isLoading}
      filters={
        <>
          <FilterInput
            value={filterText}
            onChange={setFilterText}
            placeholder="Filter references…"
          />
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 text-muted-foreground"
              onClick={() => setFilterText("")}
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </>
      }
    >
      {isLoading ? (
        <CollectionLoading label="Loading references…" />
      ) : visible.length === 0 ? (
        <CollectionEmpty
          icon={<BookOpen className="h-10 w-10 text-muted-foreground" />}
          title={hasFilters ? "Nothing matches that filter." : "No references yet."}
          hint={
            hasFilters
              ? "Try a different book or wording."
              : "References are saved as you cite scripture while writing."
          }
        />
      ) : (
        <CollectionList>
          {visible.map((reference) => (
            <CollectionRow
              key={reference.id}
              href={`/scripture-references/${reference.id}`}
              icon={<BookOpen className="h-4 w-4 text-muted-foreground" />}
              title={formatReference(reference)}
              subtitle={reference.text || reference.translation || undefined}
              meta={formatDistanceToNow(new Date(reference.created_at), {
                addSuffix: true,
              })}
            />
          ))}
        </CollectionList>
      )}
    </CollectionPage>
  );
}
